import React, { Component } from 'react';
import { Row, Col, Button, FormGroup, FormControl, Form } from 'react-bootstrap';
import './TemplateSnippet.css';
import RichTextCanvas from './RichTextCanvas';
import RichTextMenu from './RichTextMenu';
import axios from 'axios';
import config from '../config';

const SUPPORTED_FONTS = [
    'Arial',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Times New Roman',
    'Georgia',
    'Garamond',
    'Courier New',
    'Brush Script MT',
];

export class MediaTextsEditor extends Component {
    constructor(props) {
        super(props);

        this.copiedLayer = null;

        this.state = {
            media: null,
            textLayers: [],
            selectedLayerIndex: null,
            backgroundImageUrl: null,
            canvasWidth: 1920,
            canvasHeight: 1080,
            zoom: 1,
            isSaved: true,
            isProcessing: false,
            processingStatus: null,
            organisationUUID: this.props.match.params.uuid,
            mediaLibraryUUID: this.props.match.params.mediaLibraryUUID,
            isLoadingImage: true,
        };

        this.pollingInterval = null;
        this.canvasRef = React.createRef();
        this.loadingMediaUUID = null; // Track which media is currently loading
    }

    componentWillReceiveProps(newProps) {
        // If media prop changes, update state
        if (newProps.media && newProps.media.uuid &&
            (!this.props.media || newProps.media.uuid !== this.props.media.uuid)) {

            const { organisationUUID, mediaLibraryUUID } = this.state;

            const isCurrentlyProcessing = newProps.media.flatteningStatus === 'pending' ||
                                         newProps.media.flatteningStatus === 'processing';

            this.setState({
                media: newProps.media,
                textLayers: newProps.media.textLayers || [],
                processingStatus: newProps.media.flatteningStatus || 'none',
                isProcessing: isCurrentlyProcessing,
            });

            // Load background image
            this.loadBackgroundImage(newProps.media);

            // Calculate zoom to fit
            setTimeout(() => this.zoomToFit(), 100);

            // Start polling if processing
            if (isCurrentlyProcessing) {
                this.startPolling(newProps.media.uuid);
            }
        }
    }

    componentDidMount() {
        // Add keyboard event listeners for copy/paste
        document.addEventListener('keydown', this.handleKeyDown);

        // If media prop is already provided, use it
        if (this.props.media && this.props.media.uuid) {
            const { organisationUUID, mediaLibraryUUID } = this.state;

            const isCurrentlyProcessing = this.props.media.flatteningStatus === 'pending' ||
                                         this.props.media.flatteningStatus === 'processing';

            const textLayers = this.props.media.textLayers || [];

            this.setState({
                media: this.props.media,
                textLayers: textLayers,
                processingStatus: this.props.media.flatteningStatus || 'none',
                isProcessing: isCurrentlyProcessing,
            });

            this.loadBackgroundImage(this.props.media);

            // If textLayers is empty/undefined, try to fetch from API (fallback)
            if (!this.props.media.textLayers || this.props.media.textLayers.length === 0) {
                this.loadTextOverlays(this.props.media.uuid);
            }

            // Calculate zoom to fit
            setTimeout(() => this.zoomToFit(), 100);

            // Start polling if processing
            if (isCurrentlyProcessing) {
                this.startPolling(this.props.media.uuid);
            }
        } else {
            // Fallback: try to load from route param (shouldn't happen normally)
            const mediaUUID = this.props.match.params.mediaUUID;
            if (mediaUUID) {
                this.loadMediaDetails(mediaUUID);
            } else {
                console.error('No media UUID available from props or route params');
            }
        }
    }

    componentWillUnmount() {
        // Clean up keyboard event listeners
        document.removeEventListener('keydown', this.handleKeyDown);
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }
    }

    loadMediaDetails = async (mediaUUID) => {
        // Prevent double-loading the same media
        if (this.loadingMediaUUID === mediaUUID) {
            return;
        }

        try {
            this.loadingMediaUUID = mediaUUID;

            const response = await axios.get(`${config.baseURL}/media/${mediaUUID}`);
            const media = response.data.media;

            // If media doesn't have dimensions, we'll get them from the loaded image
            // Don't set default dimensions here - wait for image to load
            this.setState({
                media,
                textLayers: media.textLayers || [],
                canvasWidth: media.width || 1920, // Will be updated when image loads
                canvasHeight: media.height || 1080, // Will be updated when image loads
                processingStatus: media.flatteningStatus || 'none',
            });

            // Load background image (this will update canvas dimensions from actual image)
            this.loadBackgroundImage(media);

            // Start polling if processing
            if (media.flatteningStatus === 'pending' || media.flatteningStatus === 'processing') {
                this.startPolling(mediaUUID);
            }

            this.loadingMediaUUID = null; // Clear loading flag
        } catch (error) {
            console.error('Error loading media details:', error);
            this.loadingMediaUUID = null; // Clear loading flag on error
        }
    };

    loadTextOverlays = async (mediaUUID) => {
        try {
            const response = await axios.get(`${config.baseURL}/media/${mediaUUID}/text-overlay`);
            const textLayers = response.data.textLayers || [];

            // IMPORTANT: The text-overlay endpoint returns the FULL media object with correct
            // hasTextOverlay and originalMediaUUID fields. We need to update our media state
            // and reload the background image with this fresh data!
            const updatedMedia = {
                ...this.state.media,
                hasTextOverlay: response.data.hasTextOverlay,
                originalMediaUUID: response.data.originalMediaUUID,
                flatteningStatus: response.data.flatteningStatus,
                textLayers: textLayers
            };

            this.setState({
                textLayers,
                media: updatedMedia
            });

            // Reload background image with updated media data
            this.loadBackgroundImage(updatedMedia);
        } catch (error) {
            console.error('Error loading text overlays:', error);
            // If 404, media might not have any text overlays yet - that's ok
            if (error.response && error.response.status === 404) {
                this.setState({ textLayers: [] });
            }
        }
    };

    loadBackgroundImage = (media) => {
        const { organisationUUID, mediaLibraryUUID } = this.state;

        let backgroundImageUrl;

        // IMPORTANT: Use /mediaserver/media/ path (NOT /mediaserver/thumbnails/)
        // Thumbnails are at: /mediaserver/thumbnails/{org}/{lib}/{uuid}.jpg (always .jpg, small size)
        // Full images are at: /mediaserver/media/{org}/{lib}/{uuid}.{extension} (original extension, full size)

        // For editing: ALWAYS use original (un-flattened) image from /originals/ if it exists
        if (media.originalMediaUUID) {
            // Use the ORIGINAL (without text overlay baked in) from /originals/ folder
            backgroundImageUrl = `${config.playersBaseURL}/mediaserver/media/${organisationUUID}/${mediaLibraryUUID}/originals/${media.originalMediaUUID}.${media.extension}`;
        } else {
            // No original yet - use current image (this IS the original, not yet flattened)
            backgroundImageUrl = `${config.playersBaseURL}/mediaserver/media/${organisationUUID}/${mediaLibraryUUID}/${media.uuid}.${media.extension}`;
        }

        // Add cache buster to prevent browser from serving cached thumbnail
        backgroundImageUrl += `?t=${Date.now()}`;

        this.setState({ backgroundImageUrl });

        // Preload image to get actual dimensions
        // We use naturalWidth/naturalHeight from the loaded image because the API
        // doesn't reliably return width/height for all media
        const img = new Image();
        img.src = backgroundImageUrl;
        img.onload = () => {
            this.setState({
                canvasWidth: img.naturalWidth,
                canvasHeight: img.naturalHeight,
                isLoadingImage: false,
            }, () => {
                // Recalculate zoom now that we have correct dimensions
                this.zoomToFit();
            });
        };
        img.onerror = (err) => {
            console.error('Failed to load background image:', backgroundImageUrl, err);
            this.setState({ isLoadingImage: false });
        };
    };

    addTextLayer = () => {
        const newLayer = {
            text: 'New Text',
            x: 100,
            y: 100,
            width: null, // Auto-size based on text content
            fontSize: 48,
            fontFamily: 'Arial',
            fontWeight: 'normal',
            fontStyle: 'normal',
            textColour: '#000000',
            textAlign: 'left',
            textDecoration: 'none',
            rotation: 0,
            opacity: 1,
            strokeColour: null,
            strokeWidth: 0,
            z: this.state.textLayers.length,
        };

        this.setState((prevState) => ({
            textLayers: [...prevState.textLayers, newLayer],
            selectedLayerIndex: prevState.textLayers.length,
            isSaved: false,
        }));
    };

    removeTextLayer = () => {
        const { selectedLayerIndex, textLayers } = this.state;
        if (selectedLayerIndex !== null) {
            const newLayers = textLayers.filter((_, index) => index !== selectedLayerIndex);
            this.setState({
                textLayers: newLayers,
                selectedLayerIndex: null,
                isSaved: false,
            });
        }
    };

    updateTextLayer = (index, updates) => {
        this.setState((prevState) => {
            const newLayers = [...prevState.textLayers];
            newLayers[index] = { ...newLayers[index], ...updates };
            return {
                textLayers: newLayers,
                isSaved: false,
            };
        });
    };

    selectLayer = (index) => {
        this.setState({ selectedLayerIndex: index });
    };

    handleKeyDown = (e) => {
        // Only handle if not typing in an input/textarea
        const target = e.target;
        const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

        // Cmd/Ctrl + C - Copy
        if ((e.metaKey || e.ctrlKey) && e.key === 'c' && !isTyping) {
            const { selectedLayerIndex, textLayers } = this.state;
            if (selectedLayerIndex !== null && textLayers[selectedLayerIndex]) {
                this.copiedLayer = { ...textLayers[selectedLayerIndex] };
                console.log('Copied layer:', this.copiedLayer);
                e.preventDefault();
            }
        }

        // Cmd/Ctrl + V - Paste
        if ((e.metaKey || e.ctrlKey) && e.key === 'v' && !isTyping) {
            if (this.copiedLayer) {
                const newLayer = {
                    ...this.copiedLayer,
                    x: this.copiedLayer.x + 20, // Offset slightly so you can see it
                    y: this.copiedLayer.y + 20,
                };
                this.setState(prevState => ({
                    textLayers: [...prevState.textLayers, newLayer],
                    selectedLayerIndex: prevState.textLayers.length,
                    isSaved: false,
                }));
                console.log('Pasted layer:', newLayer);
                e.preventDefault();
            }
        }
    };

    handleDragStop = (index, position) => {
        this.updateTextLayer(index, {
            x: Math.round(position.x),
            y: Math.round(position.y),
        });
    };

    normalizeHTML = (html) => {
        // Create a temporary element to parse HTML
        const temp = document.createElement('div');
        temp.innerHTML = html;

        // Recursively process nodes to normalize tags
        const processNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent;
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();
                let result = '';

                // Get child content
                let childContent = '';
                for (let child of node.childNodes) {
                    childContent += processNode(child);
                }

                // Normalize to supported tags
                if (tagName === 'b' || tagName === 'strong') {
                    result = `<b>${childContent}</b>`;
                } else if (tagName === 'i' || tagName === 'em') {
                    result = `<i>${childContent}</i>`;
                } else if (tagName === 'u') {
                    result = `<u>${childContent}</u>`;
                } else if (tagName === 'font') {
                    // Convert font color to span
                    const color = node.getAttribute('color');
                    if (color) {
                        result = `<span style="color:${color}">${childContent}</span>`;
                    } else {
                        result = childContent;
                    }
                } else if (tagName === 'span') {
                    // Keep span if it has color style
                    const style = node.getAttribute('style');
                    if (style && style.includes('color')) {
                        // Extract just the color
                        const colorMatch = style.match(/color:\s*([^;]+)/);
                        if (colorMatch) {
                            result = `<span style="color:${colorMatch[1]}">${childContent}</span>`;
                        } else {
                            result = childContent;
                        }
                    } else {
                        result = childContent;
                    }
                } else if (tagName === 'br') {
                    result = '\n';
                } else if (tagName === 'div' || tagName === 'p') {
                    // Browsers create div/p tags for newlines in contentEditable
                    // Treat them as newlines, but only add \n before if this isn't the first element
                    result = childContent;
                } else {
                    // Strip unsupported tags, keep content
                    result = childContent;
                }

                return result;
            }

            return '';
        };

        let normalized = '';
        let isFirst = true;
        for (let child of temp.childNodes) {
            const content = processNode(child);

            // If this is a div or p element (block element), add newline before it (except for first one)
            if (child.nodeType === Node.ELEMENT_NODE) {
                const tagName = child.tagName.toLowerCase();
                if ((tagName === 'div' || tagName === 'p') && !isFirst) {
                    normalized += '\n';
                }
            }

            normalized += content;
            isFirst = false;
        }

        return normalized;
    };

    redirectToNewMedia = (newMediaUUID) => {
        const currentPath = window.location.pathname;
        const newPath = currentPath.replace(`/media/${this.state.media.uuid}`, `/media/${newMediaUUID}`);

        // Navigate to new URL (keeps query params like ?rel=player)
        window.location.href = newPath + window.location.search;
    };

    handleSave = async () => {
        const { media, textLayers } = this.state;

        if (!media) {
            console.error('No media object!');
            return;
        }

        this.setState({ isProcessing: true, isSaved: false });

        try {
            // If no text layers, DELETE the text overlay entirely
            if (textLayers.length === 0) {
                const response = await axios.delete(
                    `${config.baseURL}/media/${media.uuid}/text-overlay`
                );

                // Backend returns { success: true, message: "Text overlay removed successfully" }
                // and restores the original image
                if (response.data.success) {
                    // Reload media details to get updated state (original image restored)
                    const mediaResponse = await axios.get(`${config.baseURL}/media/${media.uuid}`);
                    const updatedMedia = mediaResponse.data.media;

                    this.setState({
                        media: updatedMedia,
                        textLayers: [],
                        processingStatus: 'none',
                        isProcessing: false,
                        isSaved: true,
                    });

                    // Reload background image (now points to original without overlay)
                    this.loadBackgroundImage(updatedMedia);

                    // Notify parent if callback exists
                    if (this.props.onPollingComplete) {
                        this.props.onPollingComplete(updatedMedia.uuid);
                    }
                } else {
                    console.error('Failed to delete text overlay');
                    this.setState({ isProcessing: false });
                }
            } else {
                // Strip out 'id' field and normalize HTML to supported tags
                const cleanedLayers = textLayers.map(layer => {
                    const { id, ...rest } = layer;
                    // Normalize HTML to only use supported tags
                    if (rest.text) {
                        rest.text = this.normalizeHTML(rest.text);
                    }
                    return rest;
                });

                const response = await axios.post(
                    `${config.baseURL}/media/${media.uuid}/text-overlay`,
                    { textLayers: cleanedLayers }
                );

                // Backend returns { mediaUUID, flatteningStatus }
                const newMediaUUID = response.data.mediaUUID;
                const flatteningStatus = response.data.flatteningStatus;

                if (newMediaUUID) {
                    // Update media state with new UUID
                    const updatedMedia = {
                        ...media,
                        uuid: newMediaUUID,
                    };

                    this.setState({
                        media: updatedMedia,
                        processingStatus: flatteningStatus || 'pending',
                        isSaved: true,
                    });

                    // Start polling with NEW UUID
                    this.startPolling(newMediaUUID);
                } else {
                    console.error('No mediaUUID in response');
                    this.setState({ isProcessing: false });
                }
            }
        } catch (error) {
            console.error('Error saving text overlay:', error);
            this.setState({ isProcessing: false });
        }
    };

    startPolling = (mediaUUID) => {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }

        this.pollingInterval = setInterval(async () => {
            try {
                const response = await axios.get(`${config.baseURL}/media/${mediaUUID}`);
                const updatedMedia = response.data.media;
                const status = updatedMedia.flatteningStatus;

                this.setState({ processingStatus: status });

                // Check for completion statuses
                if (status === 'complete' || status === 'completed' || !status) {
                    // Update media state with fresh data from server
                    this.setState({
                        isProcessing: false,
                        processingStatus: 'complete',
                        media: updatedMedia,
                        textLayers: updatedMedia.textLayers || this.state.textLayers
                    });

                    // Reload background image with updated media (now has correct originalMediaUUID)
                    this.loadBackgroundImage(updatedMedia);

                    clearInterval(this.pollingInterval);
                    this.pollingInterval = null;

                    // Notify parent that processing is complete (now backend should have updated snippet)
                    // Wait a moment to ensure state updates have propagated
                    if (this.props.onPollingComplete) {
                        setTimeout(() => {
                            this.props.onPollingComplete(updatedMedia.uuid);
                        }, 100);
                    }
                } else if (status === 'failed' || status === 'error') {
                    this.setState({ isProcessing: false, processingStatus: 'failed' });
                    clearInterval(this.pollingInterval);
                    this.pollingInterval = null;
                    alert('Text overlay processing failed. Please try again.');
                }
            } catch (error) {
                console.error('Error polling status:', error);
            }
        }, 2000);
    };

    zoomToFit = () => {
        const { canvasWidth, canvasHeight } = this.state;

        // Get available canvas wrapper space (accounting for padding and sidebar)
        // 300px sidebar + 20px gap + 40px canvas padding + sidebar scrollbar ~17px + page margins ~240px
        const availableWidth = window.innerWidth - 300 - 20 - 40 - 17 - 240;
        // Account for header, breadcrumbs, navbar and padding ~300px
        const availableHeight = window.innerHeight - 300;

        if (canvasWidth && canvasHeight && availableWidth > 0 && availableHeight > 0) {
            const widthRatio = availableWidth / canvasWidth;
            const heightRatio = availableHeight / canvasHeight;
            const zoom = Math.min(widthRatio, heightRatio, 1.0); // Don't zoom in beyond 100%

            this.setState({ zoom: Math.max(zoom, 0.1) });
        }
    };

    zoomIn = () => {
        this.setState((prevState) => ({
            zoom: Math.min(prevState.zoom + 0.1, 3),
        }));
    };

    zoomOut = () => {
        this.setState((prevState) => ({
            zoom: Math.max(prevState.zoom - 0.1, 0.3),
        }));
    };

    // Formatting methods that delegate to canvas
    applyBold = () => {
        if (this.canvasRef.current) {
            this.canvasRef.current.applyFormatting('bold');
        }
    };

    applyItalic = () => {
        if (this.canvasRef.current) {
            this.canvasRef.current.applyFormatting('italic');
        }
    };

    applyUnderline = () => {
        if (this.canvasRef.current) {
            this.canvasRef.current.applyFormatting('underline');
        }
    };

    saveSelection = () => {
        if (this.canvasRef.current) {
            return this.canvasRef.current.saveCurrentSelection();
        }
        return null;
    };

    applyColor = (color, savedRange = null) => {
        if (this.canvasRef.current) {
            this.canvasRef.current.applyFormatting('foreColor', color, savedRange);
        }
    };

    applyFontSize = (size) => {
        if (this.canvasRef.current) {
            // Convert px to font size number (1-7 for execCommand)
            const fontSize = Math.min(7, Math.max(1, Math.floor(size / 10)));
            this.canvasRef.current.applyFormatting('fontSize', fontSize);
        }
    };

    applyFontFamily = (font) => {
        if (this.canvasRef.current) {
            this.canvasRef.current.applyFormatting('fontName', font);
        }
    };

    render() {
        const {
            textLayers,
            selectedLayerIndex,
            backgroundImageUrl,
            canvasWidth,
            canvasHeight,
            zoom,
            isSaved,
            isProcessing,
            processingStatus,
            isLoadingImage,
        } = this.state;

        const selectedLayer = selectedLayerIndex !== null ? textLayers[selectedLayerIndex] : null;

        return (
            <div style={{
                display: 'flex',
                width: '100%',
                height: 'calc(100vh - 200px)',
                gap: '20px',
                padding: '0',
                position: 'relative',
            }}>
                {/* Loading spinner overlay */}
                {isLoadingImage && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '30px',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <i className="fas fa-spinner fa-spin fa-3x" style={{ marginBottom: '15px' }} />
                            <h4>Loading editor...</h4>
                        </div>
                    </div>
                )}

                <div style={{
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}>
                    <RichTextCanvas
                        ref={this.canvasRef}
                        textLayers={textLayers}
                        selectedLayerIndex={selectedLayerIndex}
                        backgroundImageUrl={backgroundImageUrl}
                        canvasWidth={canvasWidth}
                        canvasHeight={canvasHeight}
                        zoom={zoom}
                        onSelectLayer={this.selectLayer}
                        onDragStop={this.handleDragStop}
                        onUpdateLayer={this.updateTextLayer}
                        onRemoveLayer={this.removeTextLayer}
                    />
                </div>
                <div style={{
                    width: '300px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    height: '100%',
                    maxHeight: '100%',
                }}>
                    {/* Custom header content (e.g., template selector button) */}
                    {this.props.customMenuHeader && (
                        <div style={{ marginBottom: '10px' }}>
                            {this.props.customMenuHeader}
                        </div>
                    )}

                    <RichTextMenu
                        textLayers={textLayers}
                        selectedLayer={selectedLayer}
                        selectedLayerIndex={selectedLayerIndex}
                        onAddLayer={this.addTextLayer}
                        onRemoveLayer={this.removeTextLayer}
                        onUpdateLayer={this.updateTextLayer}
                        onZoomIn={this.zoomIn}
                        onZoomOut={this.zoomOut}
                        onSave={this.handleSave}
                        isSaved={isSaved}
                        isProcessing={isProcessing}
                        processingStatus={processingStatus}
                        supportedFonts={SUPPORTED_FONTS}
                        onApplyBold={this.applyBold}
                        onApplyItalic={this.applyItalic}
                        onApplyUnderline={this.applyUnderline}
                        onApplyColor={this.applyColor}
                        onSaveSelection={this.saveSelection}
                        onApplyFontSize={this.applyFontSize}
                        onApplyFontFamily={this.applyFontFamily}
                        customMenuFooter={this.props.customMenuFooter}
                    />
                </div>
            </div>
        );
    }
}

export default MediaTextsEditor;
